import React, { useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import {
  Download, Loader2, Link2, CheckCircle2, Image as ImageIcon, MessageSquare,
  Briefcase, Phone, Share2, AlertCircle, Eye, Sparkles, FileText, Upload,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Sprint C — Content Scraper.
 * Colle une URL → l'IA extrait hero/services/images/avis/contact/social → tu coches ce que tu veux importer.
 */
export default function ContentScraperPage() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  // Selection state
  const [heroSelected, setHeroSelected] = useState(false);
  const [contactSelected, setContactSelected] = useState(false);
  const [selectedServices, setSelectedServices] = useState(new Set());
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [selectedTestimonials, setSelectedTestimonials] = useState(new Set());
  const [importing, setImporting] = useState(false);

  const scrape = async () => {
    if (!url.trim()) return toast.error("URL requise");
    setLoading(true); setData(null);
    setSelectedServices(new Set()); setSelectedImages(new Set()); setSelectedTestimonials(new Set());
    setHeroSelected(false); setContactSelected(false);
    try {
      const r = await api.post("/content/scrape", { url });
      setData(r.data);
      toast.success(`Scan terminé : ${r.data.services.length} services, ${r.data.images.length} images, ${r.data.testimonials.length} avis`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Scraping échoué"); }
    finally { setLoading(false); }
  };

  const toggle = (set, setter, key) => {
    const n = new Set(set);
    if (n.has(key)) n.delete(key); else n.add(key);
    setter(n);
  };

  const doImport = async (target, items) => {
    if (items.length === 0) return toast.error("Sélectionne au moins un élément");
    setImporting(true);
    try {
      const r = await api.post("/content/import", { target, items });
      toast.success(`${r.data.inserted} importé(s), ${r.data.skipped} ignoré(s) (doublons)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import échoué"); }
    finally { setImporting(false); }
  };

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-6xl mx-auto" data-testid="content-scraper-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
          <Download className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Importer du contenu</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Scrape un site (ancien site du client ou référence) pour récupérer automatiquement services, photos, témoignages, contact…
          </p>
        </div>
      </div>

      <Card className="p-5 space-y-3">
        <Label>URL à scanner</Label>
        <div className="flex gap-2">
          <Input
            placeholder="https://ancien-site-client.fr"
            value={url}
            onChange={e => setUrl(e.target.value)}
            onKeyDown={e => e.key === "Enter" && scrape()}
            data-testid="scrape-url-input"
          />
          <Button onClick={scrape} disabled={loading} size="lg" className="bg-emerald-500 hover:bg-emerald-600" data-testid="scrape-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />}
            Scanner
          </Button>
        </div>
        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          Fonctionne sur les sites HTML classiques (SSR/statiques). Les sites React/Vue 100% SPA sans SSR retourneront peu de contenu.
        </p>
      </Card>

      {data && (
        <>
          {/* SEO */}
          <Card className="p-5 space-y-2">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-indigo-500" />
              <h3 className="font-bold">SEO & méta</h3>
            </div>
            <div className="text-sm space-y-1">
              <div><strong>Titre :</strong> {data.seo.title || "—"}</div>
              <div><strong>Description :</strong> {data.seo.description || "—"}</div>
              {data.seo.keywords && <div><strong>Keywords :</strong> {data.seo.keywords}</div>}
            </div>
          </Card>

          {/* HERO */}
          {data.hero?.title && (
            <Card className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-pink-500" />
                  <h3 className="font-bold">Hero section</h3>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={heroSelected} onCheckedChange={setHeroSelected} data-testid="hero-checkbox" />
                  <span className="text-xs">Importer dans les réglages du site</span>
                </label>
              </div>
              <div className="text-sm space-y-1 pl-6">
                <div><strong>H1 :</strong> {data.hero.title}</div>
                {data.hero.subtitle && <div><strong>Sous-titre :</strong> {data.hero.subtitle}</div>}
                {data.hero.baseline && <div><strong>Baseline :</strong> {data.hero.baseline}</div>}
              </div>
              {heroSelected && (
                <Button size="sm" onClick={() => doImport("hero", [data.hero])} disabled={importing} data-testid="hero-import-btn">
                  <Upload className="w-3 h-3 mr-1" />Importer le hero
                </Button>
              )}
            </Card>
          )}

          {/* CONTACT */}
          {(data.contact?.phone || data.contact?.email || data.contact?.address) && (
            <Card className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-sky-500" />
                  <h3 className="font-bold">Coordonnées</h3>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={contactSelected} onCheckedChange={setContactSelected} />
                  <span className="text-xs">Importer dans les réglages</span>
                </label>
              </div>
              <div className="text-sm space-y-1 pl-6">
                {data.contact.phone && <div><strong>Tél :</strong> {data.contact.phone}</div>}
                {data.contact.email && <div><strong>Email :</strong> {data.contact.email}</div>}
                {data.contact.address && <div><strong>Adresse :</strong> {data.contact.address}</div>}
                {data.contact.hours && <div><strong>Horaires :</strong> {data.contact.hours}</div>}
              </div>
              {contactSelected && (
                <Button size="sm" onClick={() => doImport("contact", [data.contact])} disabled={importing}>
                  <Upload className="w-3 h-3 mr-1" />Importer les coordonnées
                </Button>
              )}
            </Card>
          )}

          {/* SOCIAL */}
          {Object.keys(data.social || {}).length > 0 && (
            <Card className="p-5 space-y-2">
              <div className="flex items-center gap-2">
                <Share2 className="w-4 h-4 text-violet-500" />
                <h3 className="font-bold">Réseaux sociaux détectés</h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(data.social).map(([k, v]) => (
                  <Badge key={k} variant="outline" className="text-xs">
                    <strong className="capitalize">{k}</strong>&nbsp;: {v.substring(0, 50)}{v.length > 50 ? "…" : ""}
                  </Badge>
                ))}
              </div>
            </Card>
          )}

          {/* SERVICES */}
          {data.services?.length > 0 && (
            <Card className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-amber-500" />
                  <h3 className="font-bold">Services détectés ({data.services.length})</h3>
                </div>
                <Button
                  size="sm"
                  onClick={() => doImport("services", data.services.filter((_, i) => selectedServices.has(i)))}
                  disabled={selectedServices.size === 0 || importing}
                  data-testid="services-import-btn"
                >
                  <Upload className="w-3 h-3 mr-1" />Importer ({selectedServices.size})
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.services.map((s, i) => (
                  <label key={i} className={`p-3 rounded-lg border cursor-pointer transition flex gap-2 ${selectedServices.has(i) ? "bg-amber-500/10 border-amber-500" : "border-white/10 hover:border-white/20"}`}>
                    <Checkbox checked={selectedServices.has(i)} onCheckedChange={() => toggle(selectedServices, setSelectedServices, i)} data-testid={`service-checkbox-${i}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{s.title}</div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-3 mt-1">{s.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </Card>
          )}

          {/* TESTIMONIALS */}
          {data.testimonials?.length > 0 && (
            <Card className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-rose-500" />
                  <h3 className="font-bold">Témoignages ({data.testimonials.length})</h3>
                </div>
                <Button
                  size="sm"
                  onClick={() => doImport("testimonials", data.testimonials.filter((_, i) => selectedTestimonials.has(i)))}
                  disabled={selectedTestimonials.size === 0 || importing}
                >
                  <Upload className="w-3 h-3 mr-1" />Importer ({selectedTestimonials.size})
                </Button>
              </div>
              <div className="space-y-2">
                {data.testimonials.map((t, i) => (
                  <label key={i} className={`p-3 rounded-lg border cursor-pointer transition flex gap-2 ${selectedTestimonials.has(i) ? "bg-rose-500/10 border-rose-500" : "border-white/10 hover:border-white/20"}`}>
                    <Checkbox checked={selectedTestimonials.has(i)} onCheckedChange={() => toggle(selectedTestimonials, setSelectedTestimonials, i)} />
                    <div className="flex-1">
                      <div className="text-sm italic">« {t.text} »</div>
                      {t.author && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">— {t.author}</div>}
                    </div>
                  </label>
                ))}
              </div>
            </Card>
          )}

          {/* IMAGES */}
          {data.images?.length > 0 && (
            <Card className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-cyan-500" />
                  <h3 className="font-bold">Images ({data.images.length})</h3>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setSelectedImages(new Set(data.images.map((_, i) => i)))}>Tout</Button>
                  <Button size="sm" variant="outline" onClick={() => setSelectedImages(new Set())}>Aucune</Button>
                  <Button
                    size="sm"
                    onClick={() => doImport("media", data.images.filter((_, i) => selectedImages.has(i)).map(x => ({ ...x, source_url: url })))}
                    disabled={selectedImages.size === 0 || importing}
                    data-testid="images-import-btn"
                  >
                    <Upload className="w-3 h-3 mr-1" />Importer ({selectedImages.size})
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {data.images.map((img, i) => (
                  <label key={i} className={`relative rounded-lg overflow-hidden border-2 cursor-pointer transition ${selectedImages.has(i) ? "border-cyan-500" : "border-transparent hover:border-white/20"}`}>
                    <img src={img.src} alt={img.alt || ""} className="w-full h-32 object-cover" loading="lazy" onError={e => e.target.style.display = "none"} />
                    <div className="absolute top-1 right-1 bg-black/60 rounded p-0.5">
                      <Checkbox checked={selectedImages.has(i)} onCheckedChange={() => toggle(selectedImages, setSelectedImages, i)} />
                    </div>
                    {img.alt && <div className="absolute bottom-0 inset-x-0 text-[9px] bg-gradient-to-t from-black/90 to-transparent text-white p-1.5 line-clamp-2">{img.alt}</div>}
                  </label>
                ))}
              </div>
            </Card>
          )}

          {data.services.length === 0 && data.images.length === 0 && !data.hero?.title && (
            <Card className="p-5 bg-amber-500/10 border-amber-500/30">
              <div className="flex gap-2 items-start">
                <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
                <div className="text-sm">
                  Peu de contenu détecté — ce site est probablement une SPA (React/Vue) qui charge le contenu via JavaScript.
                  Pour les SPA, scraper avec Playwright headless serait nécessaire (à venir). Essaye avec un site HTML traditionnel.
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      <Card className="p-4 bg-sky-500/10 border-sky-500/30">
        <div className="flex gap-3 items-start">
          <AlertCircle className="w-5 h-5 text-sky-500 shrink-0 mt-0.5" />
          <div className="text-xs space-y-1">
            <p><strong>Rappel légal :</strong> N'importe que du contenu dont tu as le droit d'usage — typiquement <strong>l'ancien site du client</strong> que tu refais, ou des sites dont le client a fourni l'accord. Ne copie pas le contenu de concurrents.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
